import is from '@sindresorhus/is';
import { Maybe } from 'true-myth';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobArchitecture } from '~/shared/data-contract/job-architecture';

import { useUserSettingsUi } from '../../app/query-hooks/use-user-settings-ui';
import { useJobArchitectures } from '../api/use-job-architectures';

const findPersistedArchitectureId = (
    architectures: readonly JobArchitecture[],
    selectedArchitectureId: Maybe<string>,
): string | null => {
    if (selectedArchitectureId.isNothing || is.emptyString(selectedArchitectureId.value)) {
        return null;
    }

    const architectureExists = architectures.some((arch) => arch.id === selectedArchitectureId.value);

    return architectureExists ? selectedArchitectureId.value : null;
};

const getFirstArchitectureId = (architectures: readonly JobArchitecture[]): string | null => {
    const firstId = architectures[0]?.id;

    return is.nonEmptyString(firstId) ? firstId : null;
};

export const useResolveTargetArchitectureId = ({
    queriesEnabled = true,
}: {
    readonly queriesEnabled?: boolean;
} = {}): string | null => {
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const { data: uiSettings, isLoading: isUiSettingsLoading } = useUserSettingsUi(validatingCobrainerApiFetcher, {
        enabled: queriesEnabled,
    });
    const { data: architectures } = useJobArchitectures({ enabled: queriesEnabled });

    if (is.nullOrUndefined(architectures) || architectures.length === 0 || isUiSettingsLoading) {
        return null;
    }

    const selectedArchitectureId = Maybe.of(uiSettings?.selectedJobArchitectureId);
    const persistedId = findPersistedArchitectureId(architectures, selectedArchitectureId);

    return persistedId ?? getFirstArchitectureId(architectures);
};
